<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { usePlatformContact } from '@/composables/usePlatformContact'
import { useAuth } from '@/composables/useAuth'
import { useSupportTickets } from '@/composables/useSupportTickets'
import type { SupportTicket, SupportTicketFilter } from '@/types/support'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import SupportTicketCard from '@/components/organizer/support/SupportTicketCard.vue'
import NewTicketSlideOver from '@/components/organizer/support/NewTicketSlideOver.vue'
import TabStrip from '@/components/shared/TabStrip.vue'
import HeaderCreateButton from '@/components/shared/HeaderCreateButton.vue'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const { currentOrganisation } = useAuth()

const { supportEmail } = usePlatformContact()

const orgId = computed<number>(() => currentOrganisation.value?.id ?? 0)

type TabKey = SupportTicketFilter
const tabs = computed(() => [
  { key: 'all' as TabKey,     label: t('support.filter.all') },
  { key: 'open' as TabKey,    label: t('support.filter.open') },
  { key: 'waiting' as TabKey, label: t('support.filter.waiting') },
  { key: 'closed' as TabKey,  label: t('support.filter.closed') },
])

const activeTab = ref<TabKey>('all')
const slideOverOpen = ref(false)

const { tickets, loading, error, fetch } = useSupportTickets(orgId.value)

async function loadTab(tab: TabKey): Promise<void> {
  activeTab.value = tab
  await fetch(tab, 1)
}

onMounted(() => {
  void loadTab('all')

  // Deep link from the About page (issue #241): open the new-ticket panel once, then
  // drop the parameter so a refresh or back navigation does not reopen it.
  if (route.query.new === '1') {
    slideOverOpen.value = true
    const { new: _new, ...rest } = route.query
    void router.replace({ query: rest })
  }
})

function openNewTicket(): void {
  slideOverOpen.value = true
}

function handleCreated(ticket: SupportTicket): void {
  slideOverOpen.value = false
  void router.push(`/organizer/support/${ticket.id}`)
}

const emptyMessage = computed<string>(() => {
  if (activeTab.value === 'waiting') return t('support.filter.empty_waiting')
  if (activeTab.value !== 'all') return t('support.filter.empty_generic')
  return ''
})
</script>

<template>
  <OrganizerLayout>
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <!-- Page heading -->
      <div class="flex items-center justify-between mb-8">
        <div>
          <h1 class="text-3xl font-bold text-white">{{ t('support.title') }}</h1>
          <p class="mt-1 text-slate-400">{{ t('support.subtitle') }}</p>
          <i18n-t keypath="support.about_cross_link" tag="p" class="mt-1 text-sm text-slate-500">
            <template #email>
              <a :href="`mailto:${supportEmail}`" class="text-emerald-400 hover:text-emerald-300">{{ supportEmail }}</a>
            </template>
            <template #team>
              <RouterLink to="/organizer/about" class="text-emerald-400 hover:text-emerald-300">{{ t('support.about_cross_link_team') }}</RouterLink>
            </template>
          </i18n-t>
        </div>
        <HeaderCreateButton :label="t('support.new_ticket')" @click="openNewTicket" />
      </div>

      <!-- Error banner -->
      <div
        v-if="error"
        class="mb-6 rounded-xl bg-red-500/10 border border-red-500/30 px-5 py-3 text-sm text-red-400"
      >
        {{ t(error) }}
      </div>

      <!-- Filter tab strip -->
      <TabStrip
        :model-value="activeTab"
        :tabs="tabs"
        class="mb-6"
        @update:model-value="loadTab($event as TabKey)"
      />

      <!-- Skeleton loading state -->
      <div v-if="loading" class="space-y-3" aria-busy="true" :aria-label="t('common.loading')">
        <div
          v-for="i in 4"
          :key="i"
          class="rounded-xl bg-slate-800/50 border border-slate-700 px-5 py-4 animate-pulse"
        >
          <div class="flex items-start gap-4">
            <div class="flex-1 space-y-2">
              <div class="flex gap-2">
                <div class="h-4 bg-slate-700 rounded w-32" />
                <div class="h-5 bg-slate-700 rounded-full w-24" />
              </div>
              <div class="h-4 bg-slate-700 rounded w-64" />
              <div class="h-3 bg-slate-700 rounded w-48" />
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="tickets.length === 0"
        class="rounded-2xl border border-dashed border-slate-700 bg-slate-800/30 px-8 py-16 text-center"
      >
        <div class="mx-auto mb-4 w-14 h-14 rounded-full bg-slate-800 flex items-center justify-center">
          <svg class="w-7 h-7 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </div>

        <!-- All tab empty state -->
        <template v-if="activeTab === 'all'">
          <p class="text-slate-300 text-sm font-medium mb-1">{{ t('support.list.empty_title') }}</p>
          <p class="text-slate-400 text-sm mb-6">{{ t('support.list.empty_body') }}</p>
          <button
            type="button"
            class="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
            @click="openNewTicket"
          >
            {{ t('support.list.empty_cta') }}
          </button>
        </template>

        <!-- Filtered tab empty state -->
        <template v-else>
          <p class="text-slate-400 text-sm">{{ emptyMessage }}</p>
        </template>
      </div>

      <!-- Ticket list -->
      <div v-else class="space-y-3">
        <SupportTicketCard
          v-for="ticket in tickets"
          :key="ticket.id"
          :ticket="ticket"
        />
      </div>
    </main>

    <!-- New ticket slide-over -->
    <NewTicketSlideOver
      :organisation-id="orgId"
      :open="slideOverOpen"
      @close="slideOverOpen = false"
      @created="handleCreated"
    />
  </OrganizerLayout>
</template>
